import { RestError } from '@azure/core-http';
import { CourierDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useForm } from 'react-hook-form';
import { castDraft } from 'immer';
import { useImmer } from 'use-immer';
import { useMutationSetCourierApproval } from '../../../couriers.queries';
import {
  CourierApprovalFormState,
  courierApprovalOptions,
  makeCourierApprovalFormConfig,
  mapCourierApprovalFormToDto,
  mapCourierToApprovalForm
} from '../../../logic/courier-form.map';

export type EditApprovalActionState = {
  saving: boolean;
  error?: RestError | Error;
};

export interface EditApprovalActionProps {
  courier: Pick<CourierDto, 'id' | 'displayName' | 'accessLevel'>;
  /** The decision to open on, where one was already picked on the way in. */
  preselected?: string;
  onAbort(): void;
  onSuccess(): void;
}

/**
 * The approval decision on its own: the general edit refuses a rejection, since that path skips the
 * AC approval flag and the note.
 *
 * The details page and the pending list have already loaded the courier, so the form opens without
 * fetching again.
 */
export function useEditApprovalAction({ courier, preselected, onAbort, onSuccess }: EditApprovalActionProps) {
  const [state, setState] = useImmer<EditApprovalActionState>({ saving: false });

  const { control, handleSubmit } = useForm<CourierApprovalFormState>({
    defaultValues: mapCourierToApprovalForm(courier, preselected)
  });

  const formConfig = makeCourierApprovalFormConfig();

  const setApprovalMutation = useMutationSetCourierApproval(courier.id);

  const handlers = {
    onCancel: onAbort,
    onSubmit(formState: CourierApprovalFormState) {
      const decision = mapCourierApprovalFormToDto(formState, courier);

      // Nothing to decide. Sending it anyway would matter: approving sets the status to active
      // too, reinstating a courier deactivated since.
      if (!decision) {
        onAbort();
        return;
      }

      setState((draft) => {
        draft.saving = true;
      });

      setApprovalMutation.mutate(decision, {
        onSuccess() {
          onSuccess();
        },
        onError(error) {
          setState((draft) => {
            draft.saving = false;
            draft.error = castDraft(error);
          });
        }
      });
    }
  };

  return {
    state,
    handlers,
    control,
    handleSubmit,
    formConfig,
    approvalOptions: courierApprovalOptions
  } as const;
}
